import { IdentityStorageDto } from "./identity-storage-dto";
import { inject, injectWithTransform, singleton } from "tsyringe";
import {
  Logger,
  LoggerTransformer,
  ServiceBaseUrl,
  LocalStorage,
  HttpClient,
} from "@identity/utils";
import { UUID } from "@identity/symbols";

@singleton()
export class IdentityStorageClient {
  private readonly logGroup = "identity-storage";
  private readonly IdentityStorageKey = "identity";
  private readonly identityServiceUrl: string;

  constructor(
    @injectWithTransform(Logger, LoggerTransformer, "UserIdentity")
    private readonly logger: Logger,
    @inject(UUID) private readonly uuid: UuidProvider,
    @inject(LocalStorage) private readonly storage: LocalStorage,
    @inject(ServiceBaseUrl) private readonly serviceBaseUrl: ServiceBaseUrl,
    @inject(HttpClient) private readonly httpClient: HttpClient,
  ) {
    this.identityServiceUrl = this.serviceBaseUrl.get() + "identity-storage/";
  }

  async fetchData(): Promise<IdentityStorageDto> {
    try {
      const response = await this.httpClient.get(this.identityServiceUrl, {
        mode: "cors",
        credentials: "include",
      });
      if (response.status === 200 || response.status === 202) {
        return response.json();
      } else {
        return this.createFallbackResponse();
      }
    } catch (reason) {
      this.logger.info(
        this.logGroup,
        "Loading Identity LocalStorage data failed",
        reason,
      );
      return this.createFallbackResponse();
    }
  }

  async postData(data: IdentityStorageDto): Promise<IdentityStorageDto> {
    const body = { ppid: data.ppid };
    const url = this.identityServiceUrl + "identity";
    const response = await this.httpClient.put(url, body, {
      mode: "cors",
      credentials: "include",
    });
    return response.json();
  }

  getLocalData(): IdentityStorageDto {
    return this.storage.getItem(this.IdentityStorageKey) as IdentityStorageDto;
  }

  setLocalData(data: Partial<IdentityStorageDto>): void {
    this.storage.setItem(this.IdentityStorageKey, data);
  }

  private createFallbackResponse(): IdentityStorageDto {
    return {
      timestamp: Date.now(),
      synced: false,
      ppid: this.uuid(),
    };
  }
}
